import React, { useState, useCallback, useRef, useEffect } from 'react';
import { 
  Bot, 
  Globe, 
  Building, 
  Settings, 
  Play, 
  Pause, 
  Code, 
  Monitor,
  Wrench,
  Plus,
  X,
  Copy,
  Download
} from 'lucide-react';
import './Agent.css';

const accent = '#6366f1';
const accentLight = '#f1f5ff';

// Modal component moved outside to prevent re-render issues
const Modal = ({ isOpen, onClose, title, children }) => {
  if (!isOpen) return null;

  return (
    <div className="modal-backdrop" style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', background: 'rgba(0,0,0,0.5)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
      <div className="modal-content" style={{ background: '#fff', borderRadius: '12px', padding: '2rem', minWidth: 320, maxWidth: 480, width: '100%', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1), 0 10px 10px -5px rgba(0,0,0,0.04)', border: '1px solid #e5e7eb' }}>
        <div style={{ marginBottom: '1.5rem' }}>
          <h4 style={{ color: '#1f2937', fontSize: '1.25rem', fontWeight: '600', marginBottom: '0.5rem' }}>{title}</h4>
        </div>
        {children}
      </div>
    </div>
  );
};

const Agent = () => {
  const [isActive, setIsActive] = useState(false);
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [showOfficeModal, setShowOfficeModal] = useState(false);
  const [configData, setConfigData] = useState({
    geoLocation: '',
    timezone: '',
    currentTime: '',
    location: ''
  });
  const [officeData, setOfficeData] = useState({
    email: '',
    name: '',
    address: ''
  });

  const handleConfigSubmit = useCallback((e) => {
    e.preventDefault();
    setShowConfigModal(false);
  }, []);

  const handleOfficeSubmit = useCallback((e) => {
    e.preventDefault();
    setShowOfficeModal(false);
  }, []);

  const handleCopyScript = useCallback(() => {
    const scriptText = `<script src="https://your-agent-widget.com/script.js" data-agent-id="your-agent-id"></script>`;
    navigator.clipboard.writeText(scriptText);
  }, []);

  const handleConfigChange = useCallback((field, value) => {
    setConfigData(prev => ({ ...prev, [field]: value }));
  }, []);

  const handleOfficeChange = useCallback((field, value) => {
    setOfficeData(prev => ({ ...prev, [field]: value }));
  }, []);

  return (
    <div style={{ 
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif', 
      background: '#fafbfc', 
      minHeight: '100vh',
      padding: '2rem 1.5rem',
      width: '100%',
      boxSizing: 'border-box'
    }}>
      {/* Header Section */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginBottom: '1rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '1rem' }}>
          <div>
            <h1 className="fw-bold mb-2" style={{ color: '#1f2937', fontSize: '1.75rem', letterSpacing: '-0.025em' }}>Agent Configuration</h1>
            <p className="text-muted mb-0" style={{ fontSize: '0.875rem' }}>Configure your AI agent settings and preferences</p>
          </div>
          <button 
            className="btn d-flex align-items-center justify-content-center gap-2 px-3 px-md-4 py-2 fw-semibold"
            style={{ 
              background: isActive ? '#dc2626' : '#059669', 
              color: '#fff', 
              border: 'none', 
              borderRadius: '8px', 
              fontSize: '0.875rem',
              boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
              transition: 'all 0.15s ease-in-out'
            }}
            onClick={() => setIsActive(!isActive)}
          >
            {isActive ? <Pause size={16} /> : <Play size={16} />}
            <span className="d-none d-sm-inline">{isActive ? 'Deactivate' : 'Activate'}</span>
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap', marginBottom: '1rem' }}>
        <div style={{ flex: '1 1 180px', minWidth: 0 }}>
          <div className="card border-0 shadow-sm h-100" style={{ background: '#fff', borderRadius: '12px', width: '100%' }}>
            <div className="card-body p-3 p-md-4">
              <div className="d-flex align-items-center">
                <div style={{ width: 40, height: 40, background: accentLight, borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <Bot size={20} style={{ color: accent }} />
                </div>
                <div style={{ marginLeft: 12 }}>
                  <h6 className="mb-1 fw-semibold" style={{ color: '#1f2937', fontSize: '0.875rem' }}>Agent Status</h6>
                  <p className="mb-0 text-muted" style={{ fontSize: '0.75rem' }}>{isActive ? 'Active' : 'Inactive'}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div style={{ flex: '1 1 180px', minWidth: 0 }}>
          <div className="card border-0 shadow-sm h-100" style={{ background: '#fff', borderRadius: '12px', width: '100%' }}>
            <div className="card-body p-3 p-md-4">
              <div className="d-flex align-items-center">
                <div style={{ width: 40, height: 40, background: '#f0fdf4', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <Globe size={20} style={{ color: '#16a34a' }} />
                </div>
                <div style={{ marginLeft: 12 }}>
                  <h6 className="mb-1 fw-semibold" style={{ color: '#1f2937', fontSize: '0.875rem' }}>Language</h6>
                  <p className="mb-0 text-muted" style={{ fontSize: '0.75rem' }}>English</p>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div style={{ flex: '1 1 180px', minWidth: 0 }}>
          <div className="card border-0 shadow-sm h-100" style={{ background: '#fff', borderRadius: '12px', width: '100%' }}>
            <div className="card-body p-3 p-md-4">
              <div className="d-flex align-items-center">
                <div style={{ width: 40, height: 40, background: '#f0f9ff', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <Wrench size={20} style={{ color: '#0284c7' }} />
                </div>
                <div style={{ marginLeft: 12 }}>
                  <h6 className="mb-1 fw-semibold" style={{ color: '#1f2937', fontSize: '0.875rem' }}>Custom Tools</h6>
                  <p className="mb-0 text-muted" style={{ fontSize: '0.75rem' }}>3 configured</p>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div style={{ flex: '1 1 180px', minWidth: 0 }}>
          <div className="card border-0 shadow-sm h-100" style={{ background: '#fff', borderRadius: '12px', width: '100%' }}>
            <div className="card-body p-3 p-md-4">
              <div className="d-flex align-items-center">
                <div style={{ width: 40, height: 40, background: '#fef2f2', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <Monitor size={20} style={{ color: '#dc2626' }} />
                </div>
                <div style={{ marginLeft: 12 }}>
                  <h6 className="mb-1 fw-semibold" style={{ color: '#1f2937', fontSize: '0.875rem' }}>Widget Status</h6>
                  <p className="mb-0 text-muted" style={{ fontSize: '0.75rem' }}>Ready</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content Grid */}
      <div style={{ display: 'grid', gap: '1.5rem', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))' }}>
        
        {/* Basic Settings Card */}
        <div className="card border-0 shadow-sm" style={{ background: '#fff', borderRadius: '12px', overflow: 'hidden' }}>
          <div className="card-header bg-white border-0 py-3 px-4">
            <h5 className="mb-0 fw-semibold" style={{ color: '#1f2937', fontSize: '1rem' }}>Basic Settings</h5>
          </div>
          <div className="card-body p-4">
            <div style={{ marginBottom: '1rem' }}>
              <label className="modal-label">Prompt</label>
              <textarea 
                placeholder="Enter your agent prompt here..."
                rows={4}
                className="modal-input"
              />
            </div>
            
            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '1rem' }}>
              <div>
                <label className="modal-label">Webhook URL</label>
                <input 
                  type="url" 
                  placeholder="https://your-webhook-url.com"
                  className="modal-input"
                />
              </div>
              
              <div>
                <label className="modal-label">Language</label>
                <select className="modal-input">
                  <option value="en">English</option>
                  <option value="es">Spanish</option>
                  <option value="fr">French</option>
                  <option value="de">German</option>
                  <option value="it">Italian</option>
                  <option value="pt">Portuguese</option>
                  <option value="ru">Russian</option>
                  <option value="ja">Japanese</option>
                  <option value="ko">Korean</option>
                  <option value="zh">Chinese</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Configuration Card */}
        <div className="card border-0 shadow-sm" style={{ background: '#fff', borderRadius: '12px', overflow: 'hidden' }}>
          <div className="card-header bg-white border-0 py-3 px-4" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h5 className="mb-0 fw-semibold" style={{ color: '#1f2937', fontSize: '1rem' }}>Configuration</h5>
            <button 
              className="btn d-flex align-items-center gap-2 px-3 py-2"
              style={{ 
                background: '#f8fafc', 
                color: '#64748b', 
                border: '1px solid #e2e8f0', 
                borderRadius: '8px', 
                fontSize: '0.875rem',
                fontWeight: '500'
              }}
              onClick={() => setShowConfigModal(true)}
            >
              <Settings size={16} />
              Configure
            </button>
          </div>
          <div className="card-body p-4">
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', padding: '1rem', background: '#f9fafb', borderRadius: '8px' }}>
              <Globe size={20} style={{ color: accent }} />
              <div>
                <h6 style={{ color: '#1f2937', fontSize: '0.875rem', fontWeight: '600', margin: '0 0 0.25rem 0' }}>Location Settings</h6>
                <p style={{ color: '#6b7280', fontSize: '0.75rem', margin: 0 }}>Configure geo-location and timezone</p>
              </div>
            </div>
          </div>
        </div>

        {/* Office Information Card */}
        <div className="card border-0 shadow-sm" style={{ background: '#fff', borderRadius: '12px', overflow: 'hidden' }}>
          <div className="card-header bg-white border-0 py-3 px-4" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h5 className="mb-0 fw-semibold" style={{ color: '#1f2937', fontSize: '1rem' }}>Office Information</h5>
            <button 
              className="btn d-flex align-items-center gap-2 px-3 py-2"
              style={{ 
                background: '#f8fafc', 
                color: '#64748b', 
                border: '1px solid #e2e8f0', 
                borderRadius: '8px', 
                fontSize: '0.875rem',
                fontWeight: '500'
              }}
              onClick={() => setShowOfficeModal(true)}
            >
              <Building size={16} />
              Configure
            </button>
          </div>
          <div className="card-body p-4">
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', padding: '1rem', background: '#f9fafb', borderRadius: '8px' }}>
              <Building size={20} style={{ color: accent }} />
              <div>
                <h6 style={{ color: '#1f2937', fontSize: '0.875rem', fontWeight: '600', margin: '0 0 0.25rem 0' }}>Office Details</h6>
                <p style={{ color: '#6b7280', fontSize: '0.75rem', margin: 0 }}>Set up office contact information</p>
              </div>
            </div>
          </div>
        </div>

        {/* Script Tag Card */}
        <div className="card border-0 shadow-sm" style={{ background: '#fff', borderRadius: '12px', overflow: 'hidden' }}>
          <div className="card-header bg-white border-0 py-3 px-4">
            <h5 className="mb-0 fw-semibold" style={{ color: '#1f2937', fontSize: '1rem' }}>Script Tag</h5>
          </div>
          <div className="card-body p-4">
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.75rem', background: '#1f2937', borderRadius: '8px', marginBottom: '1rem' }}>
              <code style={{ flex: 1, color: '#f3f4f6', fontFamily: 'Monaco, Menlo, Ubuntu Mono, monospace', fontSize: '0.875rem', lineHeight: 1.5 }}>
                {`<script src="https://your-agent-widget.com/script.js" data-agent-id="your-agent-id"></script>`}
              </code>
              <button 
                className="btn d-flex align-items-center gap-2 px-3 py-2"
                style={{ 
                  background: accent, 
                  color: '#fff', 
                  border: 'none', 
                  borderRadius: '6px', 
                  fontSize: '0.875rem',
                  fontWeight: '500'
                }}
                onClick={handleCopyScript}
              >
                <Copy size={16} />
                Copy
              </button>
            </div>
          </div>
        </div>

        {/* Demo Widget Card */}
        <div className="card border-0 shadow-sm" style={{ background: '#fff', borderRadius: '12px', overflow: 'hidden' }}>
          <div className="card-header bg-white border-0 py-3 px-4">
            <h5 className="mb-0 fw-semibold" style={{ color: '#1f2937', fontSize: '1rem' }}>Demo Widget</h5>
          </div>
          <div className="card-body p-4">
            <div style={{ border: '1px solid #e5e7eb', borderRadius: '8px', overflow: 'hidden', background: '#fff' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.75rem', background: '#f3f4f6', borderBottom: '1px solid #e5e7eb', fontWeight: '500', color: '#374151' }}>
                <Bot size={20} />
                <span>Agent Demo</span>
              </div>
              <div style={{ padding: '1rem', textAlign: 'center' }}>
                <p style={{ margin: '0 0 1rem 0', color: '#6b7280', fontSize: '0.875rem' }}>This is a preview of how your agent will appear to users.</p>
                <button 
                  className="btn"
                  style={{ 
                    background: accent, 
                    color: '#fff', 
                    border: 'none', 
                    borderRadius: '8px', 
                    padding: '0.75rem 1.5rem', 
                    fontSize: '0.875rem',
                    fontWeight: '500'
                  }}
                >
                  Start Chat
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Custom Tools Card */}
        <div className="card border-0 shadow-sm" style={{ background: '#fff', borderRadius: '12px', overflow: 'hidden' }}>
          <div className="card-header bg-white border-0 py-3 px-4">
            <h5 className="mb-0 fw-semibold" style={{ color: '#1f2937', fontSize: '1rem' }}>Custom Tools</h5>
          </div>
          <div className="card-body p-4">
            <textarea 
              placeholder="Enter your custom tools configuration here..."
              rows={6}
              className="modal-input"
            />
          </div>
        </div>
      </div>

      {/* Configuration Modal */}
      <Modal 
        isOpen={showConfigModal} 
        onClose={() => setShowConfigModal(false)}
        title="Configuration Settings"
      >
        <form onSubmit={handleConfigSubmit}>
          <div className="modal-form-container">
            <div className="modal-form-group">
              <label className="modal-label">Geo Location</label>
              <input 
                type="text" 
                placeholder="e.g., New York, NY"
                defaultValue={configData.geoLocation}
                onChange={(e) => handleConfigChange('geoLocation', e.target.value)}
                className="modal-input"
              />
            </div>
            
            <div className="modal-form-group">
              <label className="modal-label">Timezone</label>
              <select 
                defaultValue={configData.timezone}
                onChange={(e) => handleConfigChange('timezone', e.target.value)}
                className="modal-input"
              >
                <option value="">Select timezone</option>
                <option value="UTC">UTC</option>
                <option value="America/New_York">Eastern Time</option>
                <option value="America/Chicago">Central Time</option>
                <option value="America/Denver">Mountain Time</option>
                <option value="America/Los_Angeles">Pacific Time</option>
                <option value="Europe/London">London</option>
                <option value="Europe/Paris">Paris</option>
                <option value="Asia/Tokyo">Tokyo</option>
              </select>
            </div>
            
            <div className="modal-form-group">
              <label className="modal-label">Current Time</label>
              <input 
                type="time" 
                defaultValue={configData.currentTime}
                onChange={(e) => handleConfigChange('currentTime', e.target.value)}
                className="modal-input"
              />
            </div>
            
            <div className="modal-form-group">
              <label className="modal-label">Location</label>
              <input 
                type="text" 
                placeholder="e.g., Office Building A, Floor 3"
                defaultValue={configData.location}
                onChange={(e) => handleConfigChange('location', e.target.value)}
                className="modal-input"
              />
            </div>
          </div>
          <div className="modal-button-group">
            <button 
              type="button" 
              className="modal-button modal-button-secondary"
              onClick={() => setShowConfigModal(false)}
            >
              Cancel
            </button>
            <button 
              type="submit" 
              className="modal-button modal-button-primary"
            >
              Save Configuration
            </button>
          </div>
        </form>
      </Modal>

      {/* Office Information Modal */}
      <Modal 
        isOpen={showOfficeModal} 
        onClose={() => setShowOfficeModal(false)}
        title="Office Information"
      >
        <form onSubmit={handleOfficeSubmit}>
          <div className="modal-form-container">
            <div className="modal-form-group">
              <label className="modal-label">Office Name</label>
              <input 
                type="text" 
                placeholder="e.g., Main Office"
                defaultValue={officeData.name}
                onChange={(e) => handleOfficeChange('name', e.target.value)}
                className="modal-input"
              />
            </div>
            
            <div className="modal-form-group">
              <label className="modal-label">Email</label>
              <input 
                type="email" 
                placeholder="office@company.com"
                defaultValue={officeData.email}
                onChange={(e) => handleOfficeChange('email', e.target.value)}
                className="modal-input"
              />
            </div>
            
            <div className="modal-form-group">
              <label className="modal-label">Address</label>
              <textarea 
                placeholder="Enter full office address..."
                rows={3}
                defaultValue={officeData.address}
                onChange={(e) => handleOfficeChange('address', e.target.value)}
                className="modal-input"
              />
            </div>
          </div>
          <div className="modal-button-group">
            <button 
              type="button" 
              className="modal-button modal-button-secondary"
              onClick={() => setShowOfficeModal(false)}
            >
              Cancel
            </button>
            <button 
              type="submit" 
              className="modal-button modal-button-primary"
            >
              Save Office Info
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default Agent; 